import { useCallback, useEffect, useRef, useState } from "react";
import {
  DISQUALIFYING_REVENUE,
  DISQUALIFYING_ROLE,
  wizardSteps,
  type QuestionKey,
} from "../../data/wizard";
import {
  buildLeadPayload,
  getSessionId,
  sendLeadCapture,
  type CaptureStatus,
} from "../../utils/leadCapture";
import { track, type TrackParams } from "../../utils/tracking";
import { validateUSPhone } from "../../utils/phone";

// ─── Configuración ───────────────────────────────────────────────
export const WEBHOOK_URL =
  "https://services.leadconnectorhq.com/hooks/Z34SpQXLnqpWmkXNOFgC/webhook-trigger/64a589d4-cafb-4daf-a931-02ef75a2231a";
export const CALENDAR_URL = "https://links.studiowebvip.com/widget/booking/apL3i25mr1NbW3taQCHB";
export const CALENDAR_EMBED_SCRIPT = "https://links.studiowebvip.com/js/form_embed.js";
// ─────────────────────────────────────────────────────────────────

export type Answers = Record<QuestionKey, string>;

export type Contact = {
  name: string;
  email: string;
  phone: string;
  consent: boolean;
};

export type ContactErrors = Partial<Record<keyof Contact, string>>;

export type WizardStatus = "in_progress" | "qualified" | "not_qualified";

const EMPTY_ANSWERS: Answers = {
  industry: "",
  source: "",
  responder: "",
  problem: "",
  revenue: "",
  role: "",
};
const EMPTY_CONTACT: Contact = { name: "", email: "", phone: "", consent: false };

export const TOTAL_STEPS = wizardSteps.length;
const CONTACT_STEP_INDEX = wizardSteps.findIndex((s) => s.kind === "contact");
const ADVANCE_DELAY_MS = 150;

export function useApplicationWizard() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>(EMPTY_ANSWERS);
  const [contact, setContact] = useState<Contact>(EMPTY_CONTACT);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [attempted, setAttempted] = useState(false);
  const [status, setStatus] = useState<WizardStatus>("in_progress");

  const sessionId = useRef<string | null>(null);
  const started = useRef(false);
  const leadSent = useRef(false);
  const advancing = useRef(false);
  const latest = useRef({ step, answers, contact, status, attempted });
  latest.current = { step, answers, contact, status, attempted };

  const capture = useCallback(
    (captureStatus: CaptureStatus, lastStep: number, a: Answers, c: Contact) => {
      // The CRM can only create a contact with an email or phone.
      if (!c.email.trim() && !c.phone.trim()) return;
      if (!sessionId.current) sessionId.current = getSessionId();
      sendLeadCapture(
        WEBHOOK_URL,
        buildLeadPayload({
          sessionId: sessionId.current,
          status: captureStatus,
          lastStep,
          answers: a,
          contact: c,
        })
      );
    },
    []
  );

  const finish = useCallback(
    (a: Answers, c: Contact) => {
      const qualified = isQualified(a);
      capture(qualified ? "complete" : "disqualified", TOTAL_STEPS, a, c);
      if (qualified && !leadSent.current) {
        leadSent.current = true;
        track("Lead", {
          industry: a.industry,
          revenue_bucket: a.revenue,
          role: a.role,
          problem: a.problem,
        });
      }
      if (!qualified) {
        track("Disqualified", { revenue_bucket: a.revenue, role: a.role }, { custom: true });
      }
      setStatus(qualified ? "qualified" : "not_qualified");
    },
    [capture]
  );

  const selectOption = useCallback(
    (key: QuestionKey, value: string) => {
      if (advancing.current) return;
      const { step: current, answers: prev, contact: c } = latest.current;
      const next = { ...prev, [key]: value };
      const completed = current + 1;
      setAnswers(next);

      if (current === 0 && !started.current) {
        started.current = true;
        sessionId.current = getSessionId();
        track("WizardStart", { industry: next.industry }, { custom: true });
      }
      track("WizardStep", { step: completed, ...answerParam(key, value) }, { custom: true });

      advancing.current = true;
      if (current === TOTAL_STEPS - 1) {
        window.setTimeout(() => {
          advancing.current = false;
          finish(next, c);
        }, ADVANCE_DELAY_MS);
        return;
      }
      if (current > CONTACT_STEP_INDEX) capture("partial", completed, next, c);
      window.setTimeout(() => {
        advancing.current = false;
        setStep(current + 1);
      }, ADVANCE_DELAY_MS);
    },
    [capture, finish]
  );

  const submitContact = useCallback(() => {
    setAttempted(true);
    const { step: current, answers: a, contact: c } = latest.current;
    const found = validateContact(c);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    track("WizardStep", { step: current + 1 }, { custom: true });
    capture("partial", current + 1, a, c);
    setStep(current + 1);
  }, [capture]);

  const back = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  const updateContact = useCallback(<K extends keyof Contact>(field: K, value: Contact[K]) => {
    setContact((prev) => {
      const next = { ...prev, [field]: value };
      if (latest.current.attempted) setErrors(validateContact(next));
      return next;
    });
  }, []);

  // Final "partial" capture if the visitor leaves mid-wizard.
  useEffect(() => {
    const flush = () => {
      const s = latest.current;
      if (!started.current || s.status !== "in_progress") return;
      if (s.step <= CONTACT_STEP_INDEX) return; // contact not submitted yet
      capture("partial", s.step, s.answers, s.contact);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [capture]);

  return {
    step,
    answers,
    contact,
    errors,
    status,
    selectOption,
    submitContact,
    back,
    updateContact,
  };
}

function isQualified(answers: Answers): boolean {
  return answers.revenue !== DISQUALIFYING_REVENUE && answers.role !== DISQUALIFYING_ROLE;
}

function answerParam(key: QuestionKey, value: string): TrackParams {
  switch (key) {
    case "industry":
      return { industry: value };
    case "revenue":
      return { revenue_bucket: value };
    case "role":
      return { role: value };
    case "problem":
      return { problem: value };
    default:
      return {};
  }
}

function validateContact(c: Contact): ContactErrors {
  const errs: ContactErrors = {};
  if (c.name.trim().length < 2) errs.name = "Escribe tu nombre.";
  const phoneError = validateUSPhone(c.phone);
  if (phoneError) errs.phone = phoneError;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(c.email.trim()))
    errs.email = "Escribe un email válido.";
  if (!c.consent) errs.consent = "Debes aceptar para continuar.";
  return errs;
}
